import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import {
  useHandlers,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Card, Page, Skeleton, Text } from '@ui/themed';
import { useCallback, useEffect, useState } from 'react';
import { ActionForm } from '../components/action-form';
import { useStaffApi } from '../hooks/use-staff-api';
import type { ActionDetailResponse, StaffActionCategory } from '../types';

type ViewActionPageConfig = {
  category: StaffActionCategory;
  titlePrefix: string;
  listRoutePath: string;
  submitLabel?: string;
};

function ViewActionPageInner({
  category,
  titlePrefix,
  listRoutePath,
  submitLabel,
}: ViewActionPageConfig) {
  const env = useEnv();
  const { navigationHandler, notificationHandler } = useHandlers();
  const { staffApi } = useStaffApi();
  const queryClient = useQueryClient();

  const actionType = navigationHandler.params().actionType ?? '';

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [action, setAction] = useState<{ type: string; name: string; description: string }>({
    type: '',
    name: '',
    description: '',
  });
  const [form, setForm] = useState<{
    schema: ActionDetailResponse['form']['schema'];
    data: Record<string, unknown>;
  }>({
    schema: [],
    data: {},
  });
  const [prompts, setPrompts] = useState<string[]>([]);
  const [details, setDetails] = useState<{ label: string; text: string }[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await staffApi.action(env.team.id, category, actionType);

      setAction(data.action);
      setForm({ schema: data.form.schema, data: data.form.data });
      setPrompts(data.prompts);
      setDetails(data.details);
    } catch (err) {
      if (err instanceof ApiResponseError && err.status === 403) {
        notificationHandler.notice('This action is no longer available for this team');
        navigationHandler.navigate(listRoutePath, { excludeSiteEnv: true });
        return;
      }

      notificationHandler.error('There was a problem loading this action');
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [
    staffApi,
    env.team.id,
    category,
    actionType,
    notificationHandler,
    navigationHandler,
    listRoutePath,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (payload: Record<string, unknown>) => {
    setErrors([]);
    setFormErrors({});
    setSaving(true);

    try {
      await staffApi.performAction(env.team.id, category, actionType, payload);

      notificationHandler.success(`The ${action.name} action completed successfully.`);

      // Reload env to ensure app shows latest state
      queryClient.invalidateQueries({ queryKey: ['get-env'] });
      // Reload to get latest values from server
      await load();
    } catch (_err) {
      const err = _err as {
        response?: {
          status?: number;
          data?: { result?: { errors?: string[] | Record<string, string[]> } };
        };
      };
      const status = err.response?.status;
      const resultErrors = err.response?.data?.result?.errors;

      if (status === 400) {
        notificationHandler.error(`${action.name} Action - Invalid request`);
        if (resultErrors) {
          setFormErrors(resultErrors as Record<string, string[]>);
        }
      } else if (status === 422) {
        notificationHandler.error(`${action.name} Action - Unable to complete action`);
        if (resultErrors) {
          setErrors(resultErrors as string[]);
        }
      } else {
        console.error(err);
        notificationHandler.error(
          `${action.name} Action - There was an issue completing this action.`,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton />;

  if (loadError) {
    return (
      <Alert variant="error" headingLevel="h3">
        There was a problem loading this action
      </Alert>
    );
  }

  return (
    <Page title={`${titlePrefix} — ${action.name}`} description={action.description}>
      <Card>
        {prompts.map((prompt) => (
          <Alert key={prompt} variant="warning" headingLevel="h3">
            {prompt}
          </Alert>
        ))}
        {details.length > 0 && (
          <div className="rounded bg-gray-100 p-2">
            {details.map((detail) => (
              <div key={detail.label} className="flex gap-4 text-sm">
                <Text bold>{detail.label}</Text>
                <Text variant="secondary">{detail.text}</Text>
              </div>
            ))}
          </div>
        )}
        <ActionForm
          schema={form.schema}
          data={form.data}
          errors={formErrors}
          saving={saving}
          submitLabel={submitLabel}
          onSave={handleSave}
        />
        {errors.map((error) => (
          <Alert key={error} variant="error" headingLevel="h3">
            <span className="text-red-600 text-sm">{error}</span>
          </Alert>
        ))}
      </Card>
    </Page>
  );
}

export const ManageBillingActionPage = withPageProviders(() => (
  <ViewActionPageInner
    category="manage-billing"
    titlePrefix="Manage Billing"
    listRoutePath="/staff/manage-billing"
  />
));

export const ManageTeamActionPage = withPageProviders(() => (
  <ViewActionPageInner
    category="manage-team"
    titlePrefix="Manage Team"
    listRoutePath="/staff/manage-team"
    submitLabel="Confirm"
  />
));
